import { AnalyticsBrowser, EventProperties, ID, Options, UserTraits } from '@segment/analytics-next';
import { getConsentState, hasConsent, subscribeToConsent } from './Consent';

/**
 * Segment is created but deliberately NOT loaded here.
 *
 * `AnalyticsBrowser.load()` at module scope used to fire the moment any page
 * imported this file (Button, Header and Footer all do), which meant the
 * analytics.js bundle, its cookies (`ajs_anonymous_id`) and its first page
 * call all happened before the visitor ever saw the banner. The instance is
 * now inert until `analytics` consent is granted.
 */
const segment = new AnalyticsBrowser();

let segmentLoaded = false;

const segmentWriteKey = process.env.NEXT_PUBLIC_SEGMENT_WRITE_KEY || '';

const loadSegmentIfAllowed = () => {
    if (segmentLoaded || !segmentWriteKey) return;
    if (!hasConsent('analytics')) return;

    segmentLoaded = true;
    segment.load(
        {
            writeKey: segmentWriteKey,
            cdnURL: 'https://tc.therealest.com'
        },
        {
            integrations: {
                'Segment.io': {
                    apiHost: 't.therealest.com/v1',
                    protocol: 'https'
                }
            }
        }
    );
};

if (typeof window !== 'undefined') {
    // Load as soon as consent arrives, and stop delivering once it is withdrawn.
    subscribeToConsent(() => loadSegmentIfAllowed());
}

/**
 * Consent-gated Segment facade.
 *
 * Same surface as the raw `AnalyticsBrowser` for the call sites, but every
 * method is a no-op while `analytics` consent is absent. Calls are dropped
 * rather than buffered on purpose: a buffered queue would replay pre-consent
 * browsing the moment someone later clicked Accept, which is exactly the
 * retroactive tracking the prior-consent rule forbids.
 */
export const analytics = {
    track: (event: string, properties?: EventProperties, options?: Options) => {
        if (!hasConsent('analytics')) return;
        loadSegmentIfAllowed();
        segment.track(event, properties, options);
    },
    // Positional signature kept identical to the raw Segment call it replaced
    // (`page(category, name, properties)`) so event shapes do not change.
    page: (category?: string, name?: string, properties?: EventProperties) => {
        if (!hasConsent('analytics')) return;
        loadSegmentIfAllowed();
        segment.page(category, name, properties);
    },
    identify: (userId?: ID, traits?: UserTraits, options?: Options) => {
        if (!hasConsent('analytics')) return;
        loadSegmentIfAllowed();
        segment.identify(userId, traits, options);
    },
    reset: () => {
        if (!segmentLoaded) return;
        segment.reset();
    }
};

export const trackClick = (name?: string, onClick?: (e?: React.MouseEvent<HTMLElement>) => void) => (e?: React.MouseEvent<HTMLElement>) => {
    analytics.track('Click', { name: name || e?.currentTarget.textContent || '' });
    onClick?.();
};

type DataLayerPayload = Record<string, unknown>;

/**
 * Push to the GTM dataLayer only when a category that could act on it is
 * allowed. GTM applies Consent Mode per tag on top of this, so a visitor who
 * allowed analytics but not marketing still gets analytics tags and no pixels.
 */
export const pushToDataLayer = (payload: DataLayerPayload) => {
    if (typeof window === 'undefined' || !window.dataLayer) return;
    const consent = getConsentState();
    if (!consent.analytics && !consent.marketing) return;
    window.dataLayer.push(payload);
};

type DataLayerCartItemLike = {
    item: { name: string };
    total_amount: string;
    item_amount: string;
    quantity: number;
};

export const mapCartItemsForDataLayer = (items: DataLayerCartItemLike[]) =>
    items.map((cartItem) => ({
        name: cartItem.item.name,
        total_aomunt: cartItem.total_amount,
        item_amount: cartItem.item_amount,
        quantity: cartItem.quantity
    }));
